import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createMonoAudioChain,
  disposeMonoAudioChain,
  measureVolume,
  type MonoAudioChainNodes,
} from '@/play/lib/monoPitchDetectorCore';
import {
  createOnsetDetectorState,
  detectOnset,
  onsetConfigFromSensitivity,
  type OnsetDetectorState,
} from '../lib/rhythm/onsetDetector';

export interface OnsetDetectionState {
  volume: number;
  micReady: boolean;
  micActive: boolean;
  micError: string | null;
  simulationMode: boolean;
}

const INITIAL: OnsetDetectionState = {
  volume: 0,
  micReady: false,
  micActive: false,
  micError: null,
  simulationMode: false,
};

const MIC_ACTIVE_THRESHOLD = 0.0025;

export function useOnsetDetection(sensitivity: number) {
  const [state, setState] = useState<OnsetDetectionState>(INITIAL);
  const chainRef = useRef<MonoAudioChainNodes | null>(null);
  const rafRef = useRef<number | null>(null);
  const enabledRef = useRef(false);
  const onOnsetRef = useRef<(() => void) | null>(null);
  const onsetStateRef = useRef<OnsetDetectorState>(createOnsetDetectorState());
  const sensitivityRef = useRef(sensitivity);
  sensitivityRef.current = sensitivity;

  const stopLoop = useCallback(() => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  const teardown = useCallback(() => {
    stopLoop();
    enabledRef.current = false;
    disposeMonoAudioChain(chainRef.current);
    chainRef.current = null;
    onsetStateRef.current = createOnsetDetectorState();
  }, [stopLoop]);

  const openMicChain = useCallback(async (): Promise<boolean> => {
    disposeMonoAudioChain(chainRef.current);
    chainRef.current = null;
    onsetStateRef.current = createOnsetDetectorState();

    try {
      const chain = await createMonoAudioChain();
      chainRef.current = chain;
      return true;
    } catch {
      return false;
    }
  }, []);

  const tick = useCallback(() => {
    if (!enabledRef.current) return;

    const chain = chainRef.current;
    if (!chain) {
      rafRef.current = requestAnimationFrame(tick);
      return;
    }

    chain.analyser.getFloatTimeDomainData(chain.buffer);
    const { rms, peakRms, volume } = measureVolume(chain.buffer);
    const now = performance.now();
    const config = onsetConfigFromSensitivity(sensitivityRef.current);
    const { fired, next } = detectOnset(
      { rms, peakRms, volume },
      onsetStateRef.current,
      now,
      config
    );
    onsetStateRef.current = next;

    setState((prev) => {
      const micActive = volume >= MIC_ACTIVE_THRESHOLD;
      if (prev.volume === volume && prev.micActive === micActive) return prev;
      return { ...prev, volume, micActive };
    });

    if (fired) {
      onOnsetRef.current?.();
    }

    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const startDetection = useCallback(() => {
    if (enabledRef.current) return;
    enabledRef.current = true;
    rafRef.current = requestAnimationFrame(tick);
  }, [tick]);

  const stopDetection = useCallback(() => {
    enabledRef.current = false;
    stopLoop();
    setState((prev) => ({ ...prev, volume: 0, micActive: false }));
  }, [stopLoop]);

  const requestMic = useCallback(async (): Promise<boolean> => {
    const ok = await openMicChain();
    if (ok) {
      setState((prev) => ({
        ...prev,
        micReady: true,
        micError: null,
        simulationMode: false,
      }));
      return true;
    }
    setState((prev) => ({
      ...prev,
      micReady: true,
      micError: 'Micrófono no disponible — usa la pantalla o barra espaciadora.',
      simulationMode: true,
    }));
    return false;
  }, [openMicChain]);

  const setOnOnset = useCallback((fn: (() => void) | null) => {
    onOnsetRef.current = fn;
  }, []);

  useEffect(() => () => teardown(), [teardown]);

  return {
    ...state,
    startDetection,
    stopDetection,
    requestMic,
    setOnOnset,
    teardown,
  };
}
